import { Component, HostListener, OnInit, SecurityContext, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { User } from '../../../models/user.model';
import { Subscription } from 'rxjs';
import { UserService } from '../../../../services/user-service.service';

import { CriticalAlertsService, CriticalPost } from './critical-alerts.service';
import { checkIsWebView } from '../../../../helpers/screen-utils';
import { CustomDatePipe } from '../custom-date/custom-date.pipe';
import { preparePostHtml } from '../../posts/prepare-post-html';
import { InAppLinksDirective } from '../in-app-links.directive';

@Component({
  
  imports: [
    CommonModule,
    CustomDatePipe,
    InAppLinksDirective
  ],
  selector: 'critical-alerts',
  templateUrl: './critical-alerts.component.html',
  styleUrl: './critical-alerts.component.scss'
})
export class CriticalAlertsComponent implements OnInit, OnDestroy {

  isWebView: boolean = false;
  posts: CriticalPost[];
  private pollingSubscription?: Subscription;
  private connectionStatusSubscription?: Subscription;

  constructor(
    private criticalAlertsService: CriticalAlertsService,
    private sanitizer: DomSanitizer,
    private userService: UserService,
    private router: Router) { }

  ngOnInit() {
    this.checkScreen();

    // Initialize with HTTP call
    this.loadCriticalPosts();

    // Set up polling connection if user is available
    const userId = this.userService.CurrentUserProfile?.Id;
    if (userId) {
      this.setupPollingConnection(userId);
    } else {
      // Subscribe to user profile changes in case it loads later
      this.userService.currentUserProfile$.subscribe((profile: User | null) => {
        if (profile?.Id && !this.criticalAlertsService.isConnected()) {
          this.setupPollingConnection(profile.Id);
        }
      });
    }
  }

  ngOnDestroy() {
    // Clean up subscriptions
    this.pollingSubscription?.unsubscribe();
    this.connectionStatusSubscription?.unsubscribe();
    this.criticalAlertsService.disconnect();
  }

  private loadCriticalPosts() {
    this.criticalAlertsService.getCriticalPosts().subscribe(
      (value: CriticalPost[]) => {
        this.posts = value;
        this.posts.forEach(obj => {
          obj.SanitizedDescription = this.sanitizePostDescription(obj.Description);
        })
      }
    );
  }

  private setupPollingConnection(userId: number) {
    // Connect to polling service
    this.criticalAlertsService.connectToCriticalPostsPolling(userId);

    // Subscribe to polling updates
    this.pollingSubscription = this.criticalAlertsService.getCriticalPostsUpdates().subscribe(
      (posts: CriticalPost[]) => {
        if (posts.length > 0) {
          this.posts = posts;
          this.posts.forEach(obj => {
            obj.SanitizedDescription = this.sanitizePostDescription(obj.Description);
          });
        }
      }
    );
  }

  @HostListener('window:resize')
  checkScreen() {
    this.isWebView = checkIsWebView();
  }

  sanitizePostDescription(description: string): SafeHtml {
    if (description) {
      const cleanHtml = this.sanitizer.sanitize(SecurityContext.HTML, description) || '';
      const withBreaks = cleanHtml.replace(/\n/g, "<br>");
      return this.sanitizer.bypassSecurityTrustHtml(preparePostHtml(withBreaks));
    }
    return '';
  }


  navigateToPost(postId: number) {
    this.onDismissCriticalAlertClick(postId); // Dismiss when navigating
    this.router.navigate(['/apps/posts/detail', postId]);
  }

  onDismissCriticalAlertClick(postID: number) {
    this.criticalAlertsService.dismissCriticalAlert(postID).subscribe(
      (_) => {
        let postIndex = this.posts.findIndex(x => x.Id == postID);
        if (postIndex >= 0) {
          this.posts[postIndex].IsDismissed = true;
        }
        // Note: Polling will automatically update when backend processes the dismissal
      }
    );
  }
}
